<template>
  <MortgageCalculator
    :title="t('mortgageCalculator.title').toString()"
    :description="t('mortgageCalculator.description').toString()"
    :isPlatformIs24="isPlatformIs24"
    :locale="localeValue"
    :ubsUrlParams="ubsUrlParams"
    :initialPurchasePrice="initialPurchasePrice"
    :initialHouseholdIncome="initialHouseholdIncome"
    :initialEquityCapital="initialEquityCapital"
    :offersEndpointUrl="offersEndpointUrl"
    :callMeBackFormUrlParams="callMeBackFormUrlParams"
    @interestRateChanged="handleInterestRateChanged"
    @monthlyCostDetailsClicked="handleMonthlyCostDetailsClicked"
    @checkOffersClicked="handleCheckOffersClicked"
    @arrangeConsultationClicked="handleArrangeConsultationClicked"
    @affordabilityChartTooltipClicked="handleAffordabilityChartTooltipClicked"
    @equityChartTooltipClicked="handleEquityChartTooltipClicked"
    @equityTooltipClicked="handleEquityTooltipClicked"
    @householdIncomeTooltipClicked="handleHouseholdIncomeTooltipClicked"
    @calculatorResultChanged="handleCalculatorResultChanged"
    @calculateButtonClicked="handleCalculateButtonClicked"
    @calculatorInputChanged="handleCalculatorInputChanged"
    @getOffersClicked="handleGetOffersClicked"
    @getOffersAndConsultationClicked="handleGetOffersAndConsultationClicked"
  />
</template>

<script lang="ts" setup>
import { UbsObjectType } from '@/components/PartnerIntegrations';
import { Category, Listing } from '@smg-real-estate/hg-rets-schema';
import { StorageHelper } from '@smg-real-estate/hg-search-libs';
import {
  CalculatorResult,
  CallMeBackFormUrlParams,
  MortgageCalculator,
  MortgageCalculatorResultCategory,
  UBSUrlParams,
} from '@smg-real-estate/ubs-shared-components';
import { ref } from 'vue';

import {
  GTMAction,
  GTMInteraction,
  GTMLabel,
  trackMortgageCalculatorEvent,
} from '@/helpers/GTM/TrackMortgageCalculatorEvent';
import { getListingDisplayPrice } from '@/helpers/ListingData/ListingData';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { HgLocalStorageKey } from '@/models/HgLocalStorageKey';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';

const localStorageKey = HgLocalStorageKey.MORTGAGE_CALCULATOR;
const storageHelper = new StorageHelper(StorageNamespace.detailsPage);

defineOptions({
  name: 'MortgageCalculatorContainer',
});

const { t, locale } = useI18n();

interface Props {
  listing: Listing;
  isPlatformIs24?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isPlatformIs24: false,
});

const inputValuesFromLocalStorage = storageHelper.getLocalStorageItem(localStorageKey);
const APARTMENT_TYPES = [
  Category.Apartment,
  Category.RoofFlat,
  Category.Studio,
  Category.SingleRoom,
  Category.FurnishedFlat,
  Category.Loft,
  Category.Attic,
  Category.AtticFlat,
  Category.TerraceFlat,
  Category.GrannyFlat,
];

const localeValue = ref<HgSupportedLanguage>(locale.value as HgSupportedLanguage);

const calculatorResultCategory = ref<MortgageCalculatorResultCategory | undefined>(undefined);
const initialPurchasePrice = ref<number | undefined>(getListingDisplayPrice(props.listing));
const initialHouseholdIncome = ref<number>(inputValuesFromLocalStorage?.householdIncome || 0);
const initialEquityCapital = ref<number>(inputValuesFromLocalStorage?.equityCapital || 0);
const ubsUrlParams = ref<UBSUrlParams>({
  campID: props.isPlatformIs24 ? 'Coop-ImmoScout24-Calculator' : 'Coop-Homegate-Calculator',
  city: props.listing.address?.locality,
  postalCode: props.listing.address?.postalCode,
  streetName: props.listing.address?.street,
  livingSpace: props.listing.characteristics?.livingSpace,
  numberOfRooms: props.listing.characteristics?.numberOfRooms,
  objectType: APARTMENT_TYPES.some((type) => props.listing.categories?.includes(type))
    ? UbsObjectType.APARTMENT
    : UbsObjectType.SINGLE_FAMILY_HOME,
});
const callMeBackFormUrlParams = ref<CallMeBackFormUrlParams>({
  source: 'pdp_mortgage_calculator',
  listingId: props.listing.id,
});
const offersEndpointUrl = ref<string>(
  `${import.meta.env.VUE_API_BASE_URL}/ubs/mortgages/v2/indicative-offer`,
);

const handleAffordabilityChartTooltipClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.OPEN_TOOLTIP,
    eventLabel: GTMLabel.AFFORDABILITY_RESULT,
  });
};

const handleEquityChartTooltipClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.OPEN_TOOLTIP,
    eventLabel: GTMLabel.EQUITY_RESULT,
  });
};

const handleCheckOffersClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.GO_TO_MORTGAGE_OFFERS,
    eventLabel: GTMLabel.AFFORDABLE,
  });
};

const handleArrangeConsultationClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.REQUEST_MORTGAGE_CONSULTANT,
    eventLabel: calculatorResultCategory.value,
  });
};

const handleInterestRateChanged = (_interestRate: number): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.USE_SLIDER,
    eventLabel: GTMLabel.INTEREST_RATE,
  });
};

const handleHouseholdIncomeTooltipClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.OPEN_TOOLTIP,
    eventLabel: GTMLabel.HOUSEHOLD_INCOME,
  });
};

const handleEquityTooltipClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.OPEN_TOOLTIP,
    eventLabel: GTMLabel.EQUITY,
  });
};

const handleMonthlyCostDetailsClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.OPEN_DETAILS,
    eventLabel: GTMLabel.MONTHLY_COSTS,
  });
};

const handleCalculatorResultChanged = (calculatorResult: CalculatorResult): void => {
  calculatorResultCategory.value = calculatorResult.resultCategory;
  trackMortgageCalculatorEvent(GTMInteraction.FEATURE_DISPLAYED, {
    eventInteraction: GTMInteraction.FEATURE_DISPLAYED,
    eventAction: GTMAction.MORTGAGE_RESULTS,
    eventLabel: calculatorResult.resultCategory,
    nonInteraction: true,
  });
};

const handleCalculateButtonClicked = (): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PDP_INTERACTION, {
    eventInteraction: GTMInteraction.PDP_INTERACTION,
    eventAction: GTMAction.RUN_MORTGAGE_CALCULATOR,
    eventLabel: undefined,
  });
};

const handleCalculatorInputChanged = ({
  equityCapital,
  householdIncome,
}: {
  equityCapital: number;
  householdIncome: number;
}): void => {
  storageHelper.setLocalStorageItem(localStorageKey, {
    householdIncome: householdIncome,
    equityCapital: equityCapital,
  });
};

const handleGetOffersClicked = (url: string): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PARTNER_LINK_CLICK, {
    eventInteraction: GTMInteraction.PARTNER_LINK_CLICK,
    eventAction: GTMAction.MORTGAGE_OFFERS,
    eventLabel: String(props.listing.id),
    targetUrl: url,
  });
};

const handleGetOffersAndConsultationClicked = (url: string): void => {
  trackMortgageCalculatorEvent(GTMInteraction.PARTNER_LINK_CLICK, {
    eventInteraction: GTMInteraction.PARTNER_LINK_CLICK,
    eventAction: GTMAction.MORTGAGE_OFFERS_AND_CONSULTATION,
    eventLabel: String(props.listing.id),
    targetUrl: url,
  });
};
</script>
